/**
 * Cart Manager - Unified cart functionality coordinator
 * Manages all cart-related features and ensures proper interaction
 */

(function() {
  'use strict';

  const CartManager = {
    // Configuration
    config: {
      checkoutTimeout: 8000, // 8 seconds unified timeout
      addToCartTimeout: 8000, // 8 seconds for add to cart operations
      monitorInterval: 2000, // Check every 2 seconds
      gwpEnabled: false,
      debugMode: false
    },

    // State tracking
    state: {
      isMonitoring: false,
      monitoringInterval: null,
      checkoutInProgress: false,
      lastCartUpdate: null
    },

    /**
     * Initialize the cart manager
     */
    init: function() {
      this.log('Initializing Cart Manager');
      
      // Check for GWP settings
      if (typeof theme !== 'undefined' && theme.settings && theme.settings.gwp_activate) {
        this.config.gwpEnabled = true;
      }

      // Setup event listeners
      this.setupEventListeners();
      
      // Initialize cart state fix
      this.initCartStateFix();
      
      // Initialize GWP if enabled
      if (this.config.gwpEnabled) {
        this.initGWP();
      }

      // Initialize monitoring
      this.initMonitoring();

      this.log('Cart Manager initialized successfully');
    },

    /**
     * Setup global event listeners
     */
    setupEventListeners: function() {
      // Cart update events
      const cartEvents = ['cart:updated', 'cart:build', 'cart:quantity', 'theme:cart:change'];
      
      cartEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
          this.handleCartUpdate(eventName);
        });
      });

      // Fix for browser back/forward navigation
      window.addEventListener('pageshow', (evt) => {
        if (evt.persisted) {
          // Remove stuck loading states when returning via history
          document.body.classList.remove('unloading');
          document.querySelectorAll('.btn--loading').forEach(el => {
            el.classList.remove('btn--loading');
            el.removeAttribute('aria-busy');
          });
          this.resetCartState();
        }
      });

      // Add unloading class on navigation
      window.addEventListener('beforeunload', () => {
        document.body.classList.add('unloading');
      });

      // Checkout button clicks
      document.addEventListener('click', (e) => {
        if (e.target.matches('.cart__checkout') || e.target.closest('.cart__checkout')) {
          this.handleCheckoutClick(e);
        }
      });

      // GWP remove buttons
      document.addEventListener('click', (e) => {
        if (e.target.matches('.gwp_remove') || e.target.closest('.gwp_remove')) {
          this.handleGWPRemove(e);
        }
      });

      // Quantity changes
      document.addEventListener('click', (e) => {
        if (e.target.matches('.js-qty__adjust')) {
          this.handleQuantityChange(e);
        }
      });
    },

    /**
     * Initialize cart state fix functionality
     */
    initCartStateFix: function() {
      // Reset cart state function
      this.resetCartState = function() {
        const forms = document.querySelectorAll('[data-location]');
        
        forms.forEach(form => {
          if (form.CartForm) {
            form.CartForm.cartItemsUpdated = false;
            this.log('Reset cartItemsUpdated flag for form', form.dataset.location);
          }
        });

        // Clear stuck checkout buttons
        this.clearStuckButtons();
      };

      // Intercept fetch for cart operations
      this.interceptCartFetch();
      
      // Override add to cart for better error handling
      this.overrideAddToCart();
    },

    /**
     * Intercept fetch requests to cart endpoints
     */
    interceptCartFetch: function() {
      const originalFetch = window.fetch;
      const manager = this;

      window.fetch = function() {
        const args = arguments;
        const url = args[0];
        
        if (typeof url === 'string' && (
          url.includes('/cart/change') || 
          url.includes('/cart/update') || 
          url.includes('/cart/add')
        )) {
          return originalFetch.apply(this, args).then(response => {
            // Reset cart state after successful cart operation
            setTimeout(() => {
              manager.resetCartState();
            }, 500);
            
            return response;
          });
        }
        
        return originalFetch.apply(this, args);
      };
    },

    /**
     * Initialize GWP functionality
     */
    initGWP: function() {
      this.log('Initializing GWP functionality');
      
      // Initialize carousel if needed
      this.initGWPCarousel();
      
      // Initialize accordion
      this.initGWPAccordion();
    },

    /**
     * Initialize GWP carousel
     */
    initGWPCarousel: function() {
      if (typeof Splide === 'undefined') return;

      const initCarousel = () => {
        const splideElement = document.querySelector('.splide-cart-drawer');
        if (!splideElement) return;

        const splide = new Splide('.splide-cart-drawer', {
          perPage: 3,
          perMove: 1,
          arrows: false,
          breakpoints: {
            750: { perPage: 2 }
          }
        });

        const bar = splideElement.querySelector('.my-slider-progress-bar');
        if (bar) {
          splide.on('mounted move', function() {
            const end = splide.Components.Controller.getEnd() + 1;
            const rate = Math.min((splide.index + 1) / end, 1);
            bar.style.width = String(100 * rate) + '%';
          });
        }

        splide.mount();
      };

      // Initialize on DOM ready and drawer open
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCarousel);
      } else {
        initCarousel();
      }

      document.addEventListener('drawerOpen.CartDrawer', initCarousel);
    },

    /**
     * Initialize GWP accordion
     */
    initGWPAccordion: function() {
      class Accordion {
        constructor(el) {
          this.el = el;
          this.summary = el.querySelector('summary.sidecart-btn-toggle');
          this.content = el.querySelector('.sidecart-content-toggle');
          
          if (!this.summary || !this.content) return;
          
          this.animation = null;
          this.isClosing = false;
          this.isExpanding = false;
          
          this.summary.addEventListener('click', (e) => this.onClick(e));
        }

        onClick(e) {
          e.preventDefault();
          this.el.style.overflow = 'hidden';
          
          if (this.isClosing || !this.el.open) {
            this.open();
          } else if (this.isExpanding || this.el.open) {
            this.shrink();
          }
        }

        shrink() {
          this.isClosing = true;
          const startHeight = `${this.el.offsetHeight}px`;
          const endHeight = `${this.summary.offsetHeight}px`;
          
          if (this.animation) {
            this.animation.cancel();
          }
          
          this.animation = this.el.animate({
            height: [startHeight, endHeight]
          }, {
            duration: 400,
            easing: 'ease-out'
          });
          
          this.animation.onfinish = () => this.onAnimationFinish(false);
          this.animation.oncancel = () => this.isClosing = false;
        }

        open() {
          this.el.style.height = `${this.el.offsetHeight}px`;
          this.el.open = true;
          window.requestAnimationFrame(() => this.expand());
        }

        expand() {
          this.isExpanding = true;
          const startHeight = `${this.el.offsetHeight}px`;
          const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
          
          if (this.animation) {
            this.animation.cancel();
          }
          
          this.animation = this.el.animate({
            height: [startHeight, endHeight]
          }, {
            duration: 400,
            easing: 'ease-out'
          });
          
          this.animation.onfinish = () => this.onAnimationFinish(true);
          this.animation.oncancel = () => this.isExpanding = false;
        }

        onAnimationFinish(open) {
          this.el.open = open;
          this.animation = null;
          this.isClosing = false;
          this.isExpanding = false;
          this.el.style.height = this.el.style.overflow = '';
        }
      }

      // Initialize accordions
      const initAccordions = () => {
        document.querySelectorAll('details.sidecart-details').forEach((el) => {
          if (!el.accordion) {
            el.accordion = new Accordion(el);
          }
        });
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAccordions);
      } else {
        initAccordions();
      }

      document.addEventListener('drawerOpen.CartDrawer', initAccordions);
    },

    /**
     * Initialize monitoring for stuck buttons
     */
    initMonitoring: function() {
      // Start monitoring when needed
      this.startMonitoring = () => {
        if (this.state.isMonitoring) return;
        
        this.state.isMonitoring = true;
        this.state.monitoringInterval = setInterval(() => {
          this.checkForStuckButtons();
        }, this.config.monitorInterval);
      };

      // Stop monitoring
      this.stopMonitoring = () => {
        if (this.state.monitoringInterval) {
          clearInterval(this.state.monitoringInterval);
          this.state.monitoringInterval = null;
        }
        this.state.isMonitoring = false;
      };
    },

    /**
     * Check for stuck checkout buttons
     */
    checkForStuckButtons: function() {
      const checkoutBtns = document.querySelectorAll('.cart__checkout.btn--loading');
      
      if (checkoutBtns.length === 0) {
        this.stopMonitoring();
        return;
      }

      checkoutBtns.forEach(btn => {
        const form = btn.closest('form');
        if (!form || form.submitting) return;

        const loadingTime = btn.dataset.loadingTime;
        if (loadingTime) {
          const elapsed = Date.now() - parseInt(loadingTime);
          if (elapsed > this.config.checkoutTimeout) {
            this.log('Found stuck checkout button, resetting');
            btn.classList.remove('btn--loading');
            delete btn.dataset.loadingTime;
            this.resetCartState();
          }
        } else {
          btn.dataset.loadingTime = Date.now();
        }
      });
    },

    /**
     * Clear all stuck buttons
     */
    clearStuckButtons: function() {
      const checkoutBtns = document.querySelectorAll('.cart__checkout.btn--loading');
      
      checkoutBtns.forEach(btn => {
        const form = btn.closest('form');
        if (!form || !form.submitting) {
          btn.classList.remove('btn--loading');
          btn.setAttribute('aria-busy', 'false');
          delete btn.dataset.loadingTime;
        }
      });
      
      // Also clear any other stuck loading buttons
      document.querySelectorAll('.btn--loading').forEach(btn => {
        if (btn.dataset.loadingTime) {
          const elapsed = Date.now() - parseInt(btn.dataset.loadingTime);
          if (elapsed > this.config.checkoutTimeout) {
            btn.classList.remove('btn--loading');
            btn.setAttribute('aria-busy', 'false');
            delete btn.dataset.loadingTime;
          }
        }
      });
    },

    /**
     * Handle cart update events
     */
    handleCartUpdate: function(eventName) {
      this.log('Cart update event:', eventName);
      this.state.lastCartUpdate = Date.now();
      
      // Reset cart state
      setTimeout(() => {
        this.resetCartState();
      }, 100);
    },

    /**
     * Handle checkout button clicks
     */
    handleCheckoutClick: function(e) {
      const btn = e.target.matches('.cart__checkout') ? 
                  e.target : e.target.closest('.cart__checkout');
      const form = btn.closest('form');
      
      if (form) {
        form.submitting = true;
        this.state.checkoutInProgress = true;
        
        // Start monitoring
        this.startMonitoring();
        
        // Add aria-busy for accessibility
        btn.setAttribute('aria-busy', 'true');
        
        // Safety timeout
        setTimeout(() => {
          if (form.submitting) {
            form.submitting = false;
            this.state.checkoutInProgress = false;
            btn.classList.remove('btn--loading');
            btn.setAttribute('aria-busy', 'false');
            this.resetCartState();
            this.log('Checkout safety timeout triggered');
          }
        }, this.config.checkoutTimeout);
      }
    },

    /**
     * Handle GWP product removal
     */
    handleGWPRemove: function(e) {
      e.preventDefault();
      
      // Reset cart state after removal
      setTimeout(() => {
        this.resetCartState();
        document.dispatchEvent(new Event('cart:gwp:removed'));
      }, 500);
    },

    /**
     * Handle quantity changes
     */
    handleQuantityChange: function(e) {
      const wrapper = e.target.closest('.js-qty__wrapper');
      if (wrapper) {
        wrapper.setAttribute('aria-busy', 'true');
        wrapper.classList.add('is-loading');
        
        // Clear busy state after timeout
        setTimeout(() => {
          wrapper.setAttribute('aria-busy', 'false');
          wrapper.classList.remove('is-loading');
        }, this.config.checkoutTimeout);
      }
    },

    /**
     * Override add to cart for better error handling and accessibility
     */
    overrideAddToCart: function() {
      if (!theme.AjaxProduct || !theme.AjaxProduct.prototype) return;
      
      const originalAddItem = theme.AjaxProduct.prototype.addItemFromForm;
      const manager = this;
      
      theme.AjaxProduct.prototype.addItemFromForm = function(evt, callback) {
        evt.preventDefault();

        if (status.loading) return;

        // Add loading state with accessibility
        this.addToCart.classList.add('btn--loading');
        this.addToCart.setAttribute('aria-busy', 'true');
        status.loading = true;

        const data = theme.utils.serialize(this.form);
        
        // Add timeout protection
        const loadingTimeout = setTimeout(() => {
          status.loading = false;
          this.addToCart.classList.remove('btn--loading');
          this.addToCart.setAttribute('aria-busy', 'false');
          this.error({ description: 'Request timeout. Please try again.' });
        }, manager.config.addToCartTimeout);

        fetch(theme.routes.cartAdd, {
          method: 'POST',
          body: data,
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(response => response.json())
        .then(data => {
          clearTimeout(loadingTimeout);
          
          if (data.status === 422) {
            this.error(data);
          } else {
            this.success(data);
          }

          status.loading = false;
          this.addToCart.classList.remove('btn--loading');
          this.addToCart.setAttribute('aria-busy', 'false');

          // Reload page if adding product from cart page
          if (document.body.classList.contains('template-cart')) {
            window.scrollTo(0, 0);
            location.reload();
          }
        })
        .catch(error => {
          clearTimeout(loadingTimeout);
          status.loading = false;
          this.addToCart.classList.remove('btn--loading');
          this.addToCart.setAttribute('aria-busy', 'false');
          this.error({ description: 'Network error. Please check your connection and try again.' });
        });

        if (typeof callback === 'function') {
          callback();
        }
      };
    },

    /**
     * Debug logging
     */
    log: function() {
      if (this.config.debugMode || window.location.search.includes('debug=cart')) {
        console.log('[Cart Manager]', ...arguments);
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartManager.init());
  } else {
    CartManager.init();
  }

  // Expose to global scope for debugging
  window.CartManager = CartManager;
})();